import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import ScheduleNewManualJobData from './ScheduleNewManualJobModal';
import ScheduleNewAddConfirmModalData from './ScheduleNewAddConfirmModal';
import authService from './api-authorization/AuthorizeService';

interface AddNewProps {
    scheduleDate: string;
    dateFormatted: string;
    scheduleTypes: GenericList[];
    showModal: boolean;
    hideModal(): void;
    save(items: AddNewResults[]): void;
    saveManual(manualJob: NewItem): void;
}

interface AddNewModal {
    itemType: string;
    jobIdSearch: string;
    siteAddressSearch: string;
    manualQuoteNumberSearch: string;
    customerSearch: string;
    showResults: boolean;
    results: AddNewResults[];
    newManualItem: NewItem;
    showConfirmDetails: boolean;
    confirmJobIds: ConfirmJobIdList[];
    errors: any;
    loading: boolean;
}

interface ConfirmJobIdList {
    jobId: number;
    hasInstall: boolean;    //at least one install for this job - need to show payment method for installs only (prepaid customer)
}

interface CustomerContactList {
    id: number;
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    mobile: string;
}

interface AddNewResults {
    itemIndex: number;
    selected: boolean;
    scheduleId: number;
    scheduleDate: string;
    jobQuoteItemId: number;
    jobId: number;
    siteAddress: string;
    customer: string;
    item: string;
    itemShortcut: string;
    type: string;
    typeId: number;
    quantityTotal: number;
    quantityScheduled: number;
    quantityType: string;
    hirePeriod: string;
    hirePeriodActual: number;
    chargePublicHols: boolean;
    shutdownPeriodType: number;
    onoffhireDate: string;
    endhireDate: string;
    pickupDate: string;
    bringForwardDate: string;
    bringForwardDateFormatted: string;
    pickupScheduled: boolean;
    callout: boolean;
    allowOnGoingHire: boolean;
    notes: string;
}

interface NewItem {
    jobId: number;
    siteAddress: string;
    customerName: string;
    contactName: string;
    scheduleDate: string;
    description: string;
    typeId: number;
    notes: string;
    runsheetNotes: string;
    errors: any;
    subId: string;
}

interface GenericList {
    id: number;
    name: string;
}

export default class AddNewScheduleData extends React.Component<AddNewProps, AddNewModal> {
    constructor(props: AddNewProps) {
        super(props);
        this.state = {
            itemType: "quoted",
            jobIdSearch: "",
            siteAddressSearch: "",
            manualQuoteNumberSearch: "",
            customerSearch: "",
            showResults: false,
            newManualItem: {
                jobId: 0,
                siteAddress: "",
                customerName: "",
                contactName: "",
                scheduleDate: "",
                description: "",
                typeId: 0,
                notes: "",
                runsheetNotes: "",
                errors: {},
                subId: ""
            },
            results: [],
            showConfirmDetails: false,
            confirmJobIds: [],
            errors: {},
            loading: false
        }

        
    }

    handleChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        let change: any = {};
        change[e.target.name] = e.target.value;
        change["errors"] = errors;
        this.setState(change);
    }

    onKeyUp = (e: any) => {
        //check if enter key has been pressed and if so call search
        if (e.keyCode === 13) {
            this.search(e);
        }
    }

    search = (e: any) => {
        e.preventDefault();

        if (!this.state.jobIdSearch && !this.state.siteAddressSearch && !this.state.manualQuoteNumberSearch && !this.state.customerSearch) {
            toast.error("Please enter a Search criteria before searching!");
        } else {
            this.searchJobs();
        } 
    }

    async searchJobs() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/Schedule/GetItemsForScheduling?JobId=' + this.state.jobIdSearch + '&SiteAddress=' + this.state.siteAddressSearch + '&ManualQuoteNumber=' + this.state.manualQuoteNumberSearch + '&Customer=' + this.state.customerSearch + '&ScheduleDate=' + this.props.scheduleDate + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                var items = res.data.items;
                for (var i = 0; i < items.length; i++) {
                    items[i].itemIndex = i;
                };
                this.setState({ showResults: true, results: items })
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    selectItem = (e: any, index: number) => {
        var results = this.state.results;
        var result = results[index];
        result.selected = e.target.checked;
        this.setState({ results: results });
    }

    hideModal = () => {
        this.setState({ itemType: "quoted", jobIdSearch: "", siteAddressSearch: "", manualQuoteNumberSearch: "", customerSearch: "", showResults: false, results: [], showConfirmDetails: false, confirmJobIds: [], errors: {} });
        this.props.hideModal();
    }

    //grid item changed
    onCellChange = (index: number, item: string, value: any) => {
        //update item that has changed
        var results = this.state.results;
        var result = results[index];
        let newItem: any = {};
        newItem = result;
        newItem[item] = value;

        if (item == "onoffhireDate" && result.typeId === 1 && DateHelpers.isValidDate(value)) {
            //need to check if overhire is available, if not, then calculate the offhire date based on hire period (actual) - exclude callout items, items with pickups already scheduled and any items that can go into overhire
            if (!result.allowOnGoingHire && !result.callout && !result.pickupScheduled) {
                this.calcOffhireDate(results, result, value);
            } else {
                this.setState({ results: results, errors: {} });
            }
        } else {
            this.setState({ results: results, errors: {} });
        } 
    }

    async calcOffhireDate(results: AddNewResults[], result: AddNewResults, value: any) {
        const token = await authService.getAccessToken();

        axios.get('api/Schedule/CalculateOffhireDate?OnhireDate=' + value + '&ChargePublicHols=' + result.chargePublicHols + '&ShutdownPeriodType=' + result.shutdownPeriodType + '&HirePeriod=' + result.hirePeriodActual, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            result.endhireDate = res.data;
            this.setState({ results: results, errors: {} });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //CONFIRM DETAILS
    showConfirmJobDetails = async () => {
        //need to loop through the jobs to get details
        var jobs = this.state.results.filter(j => j.selected);
        var jobIds: ConfirmJobIdList[] = [];
        for (var i = 0; i < jobs.length; i++) {
            //check if it exists as don't need to add it twice
            var exists = jobIds.find(j => j.jobId === jobs[i].jobId);
            if (exists) {
                if (jobs[i].typeId === 1) {
                    exists.hasInstall = true;
                }
            } else {
                var newJobId: ConfirmJobIdList = {
                    jobId: jobs[i].jobId,
                    hasInstall: jobs[i].typeId === 1
                };
                jobIds.push(newJobId);
            }
        }

        this.setState({ confirmJobIds: jobIds, showConfirmDetails: true });
    }

    saveConfirmDetails = () => {
        var manualJob = this.resetManualJob();
        var items = this.state.results.filter(i => i.selected);
        this.setState({ itemType: "quoted", jobIdSearch: "", siteAddressSearch: "", manualQuoteNumberSearch: "", customerSearch: "", showResults: false, results: [], newManualItem: manualJob, showConfirmDetails: false, confirmJobIds: [], errors: {} });
        this.props.save(items);
    }


    //END CONFIRM DETAILS
    
    resetManualJob = () => {
        var manualJob: NewItem = {
            jobId: 0,
            siteAddress: "",
            customerName: "",
            contactName: "",
            scheduleDate: "",
            description: "",
            typeId: 0,
            notes: "",
            runsheetNotes: "",
            errors: {},
            subId: ""
        }
        return manualJob;
    }

    updateManualJob = (newItem: NewItem) => {
        this.setState({ newManualItem: newItem });
    }

    validate = () => {
        var items = this.state.results.filter(i => i.selected);
        if (items.length === 0) {
            toast.error("Please select at least one job to Schedule");
            return false;
        }

        let errors: any = {};
        let formIsValid = true;
        //var rowNumber = 0;

        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < items.length; i++) {
            //rowNumber += 1;
            var newErrorString = "";

            //check to make sure on/off hire date set for selected items
            if (!items[i].onoffhireDate) {
                newErrorString += "On/Off Hire Date, ";
                error = true;
            } else if (!DateHelpers.isValidDate(items[i].onoffhireDate)) {
                newErrorString += " Valid On/Off Hire Date, ";
                error = true;
            } else if (items[i].type === "Install" && items[i].onoffhireDate) {
                //validate onhire date >= Installation Date
                if (!DateHelpers.compareDate(this.props.scheduleDate, items[i].onoffhireDate)) {
                    newErrorString += " Onhire Date must be >= Installation Date, ";
                    error = true;
                }
            } else if (items[i].type === "Pick Up" && items[i].onoffhireDate) {
                //validate Dismantle Date >= Offhire date
                if (!DateHelpers.compareDate(items[i].onoffhireDate, this.props.scheduleDate)) {
                    newErrorString += " Offhire Date must be <= Dismantle Date, ";
                    error = true;
                }
            }

            if (items[i].bringForwardDate && !DateHelpers.isValidDate(items[i].bringForwardDate)) {
                newErrorString += " Valid Bring Forward Date, ";
                error = true;
            }

            if (items[i].type === "Install" && !items[i].allowOnGoingHire && !items[i].callout && !items[i].pickupScheduled) {
                if (!items[i].endhireDate) {
                    newErrorString += " End Hire Date (Customer Rule), ";
                    error = true;
                }
                if (!items[i].pickupDate) {
                    newErrorString += " Pickup Date (Customer Rule), ";
                    error = true;
                }
            }
            if (items[i].endhireDate) {
                //check they are valid dates
                if (!DateHelpers.isValidDate(items[i].endhireDate)) {
                    newErrorString += " Valid End Hire Date (Customer Rule), ";
                    error = true;
                } else if (items[i].onoffhireDate && !DateHelpers.compareDateGreater(items[i].onoffhireDate, items[i].endhireDate)) {
                    newErrorString += " End Hire Date must be > Installation Date (Customer Rule), ";
                    error = true;
                }
            }

            if (items[i].pickupDate) {
                //check they are valid dates
                if (!DateHelpers.isValidDate(items[i].pickupDate)) {
                    newErrorString += " Valid Pickup Date (Customer Rule), ";
                    error = true;
                } else if (items[i].pickupDate && !DateHelpers.compareDate(items[i].endhireDate, items[i].pickupDate)) {
                    newErrorString += " Pickup Date must be >= End Hire Date, ";
                    error = true;
                }
            }

            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + (items[i].itemIndex + 1) + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }
            
        }
        if (error) {
            formIsValid = false;
            errors["results"] = errorString;
        }
        this.setState({ errors: errors });
        return formIsValid;
    }

    validateManualJob = () => {
        let fields = this.state.newManualItem;
        let errors: any = {};
        let formIsValid = true;

        if (fields.jobId === 0 && !fields.siteAddress) {
            formIsValid = false;
            errors["jobId"] = "Job Id or Site Address is required";
        }

        if (!fields.description) {
            formIsValid = false;
            errors["description"] = "Description is required";
        }

        if (fields.typeId <= 0) {
            formIsValid = false;
            errors["typeId"] = "Type is required";
        }

        var manualItem = this.state.newManualItem;
        manualItem.errors = errors;

        this.setState({ newManualItem: manualItem });
        return formIsValid;
    }

    schedule = (e: any) => {
        e.preventDefault();
        if (this.state.itemType === "quoted") {
            //save quoted job
            if (this.validate()) {
                this.showConfirmJobDetails();
            } else {
                toast.error("Please fix validation errors before Scheduling");
            }
        } else {
            //save manual job
            if (this.validateManualJob()) {
                var newManualJob = this.state.newManualItem;
                newManualJob.scheduleDate = this.props.scheduleDate;
                var manualJob = this.resetManualJob();
                this.setState({ itemType: "quoted", jobIdSearch: "", siteAddressSearch: "", manualQuoteNumberSearch: "", customerSearch: "", showResults: false, results: [], newManualItem: manualJob, errors: {} });
                this.props.saveManual(newManualJob);
            } else {
                toast.error("Please fix validation errors before Scheduling");
            }
        }
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();

        return (<div>
            {contents}
        </div>);
    }

    renderDetails = () => {
        var minDate = DateHelpers.addDaysToCurrentDate(-90);
        var manualJob = <ScheduleNewManualJobData newItem={this.state.newManualItem} scheduleTypes={this.props.scheduleTypes} updateData={this.updateManualJob} />
        var confirmJobDetails = <ScheduleNewAddConfirmModalData jobIds={this.state.confirmJobIds} systemCompanyId={2} saveDetails={this.saveConfirmDetails} />

        return (
            <div>
                
                <div className="static-modal">
                    <Modal size="xl" show={this.props.showModal} onHide={this.hideModal}>
                        <Modal.Header className="modal__header--colour">
                            <Modal.Title>Schedule Job/s for {this.props.dateFormatted}</Modal.Title>
                            <button type="button" className="close" onClick={this.hideModal}>&times;</button>
                        </Modal.Header>
                        <Modal.Body>
                            <div>
                                <div className={this.state.showConfirmDetails ? "" : "hidden"}>
                                    {confirmJobDetails}
                                </div>
                                <div className={this.state.showConfirmDetails ? "hidden" : ""}>
                                    <div className="makeitflex">
                                        <label className="input-group" htmlFor="customer">
                                            <span className="label">Item Type </span>
                                            <input className="marginRight10" type="radio" id="quoted" name="itemType" value="quoted" checked={this.state.itemType === 'quoted'} onChange={(e) => this.handleChange(e)}></input>
                                            <label className="label__radio marginRight10" htmlFor="quoted">Quoted</label>
                                            <input className=" marginRight10" type="radio" id="manual" name="itemType" value="manual" checked={this.state.itemType === 'manual'} onChange={(e) => this.handleChange(e)}></input>
                                            <label className="label__radio" htmlFor="manual">Manual</label>
                                        </label>
                                        <button className="popup-button--yes" onClick={this.schedule}>Schedule</button>
                                    </div>
                                    <div className={this.state.itemType === "quoted" ? "" : "hidden"}>
                                        <div className="makeitflex">
                                            <label className="input-group--inline" htmlFor="jobIdSearch">
                                                <span className="label">Job Id</span>
                                                <input className="input schedulenew__addSearch--width-sm" type="text" id="jobIdSearch" name="jobIdSearch" value={this.state.jobIdSearch} onChange={(e) => this.handleChange(e)} onKeyUp={(e) => this.onKeyUp(e)}></input>
                                            </label>
                                            <label className="input-group--inline" htmlFor="siteAddressSearch">
                                                <span className="label label--setsizesm">Site Address</span>
                                                <input className="input schedulenew__addSearch--width-lg" type="text" id="siteAddressSearch" name="siteAddressSearch" value={this.state.siteAddressSearch} onChange={(e) => this.handleChange(e)} onKeyUp={(e) => this.onKeyUp(e)}></input>
                                            </label>
                                            <label className="input-group--inline" htmlFor="manualQuoteNumberSearch">
                                                <span className="label">Manual Quote #</span>
                                                <input className="input schedulenew__addSearch--width-sm" type="text" id="manualQuoteNumberSearch" name="manualQuoteNumberSearch" value={this.state.manualQuoteNumberSearch} onChange={(e) => this.handleChange(e)} onKeyUp={(e) => this.onKeyUp(e)}></input>
                                            </label>
                                            <label className="input-group--inline" htmlFor="customerSearch">
                                                <span className="label label--setsizesm">Customer</span>
                                                <input className="input schedulenew__addSearch--width-lg" type="text" id="customerSearch" name="customerSearch" value={this.state.customerSearch} onChange={(e) => this.handleChange(e)} onKeyUp={(e) => this.onKeyUp(e)}></input>
                                            </label>
                                            <button className="defaultbutton defaultbutton__small allocate__searchMargin" type='button' onClick={(e) => this.search(e)}>Search</button>
                                        </div>
                                        <div className={this.state.showResults ? "" : "hidden"}>
                                            <div className={this.state.results.length > 0 ? 'overflowAuto schedulenew__result--height' : 'hidden'}>
                                                <table className='table tableColours-header'>
                                                    <thead>
                                                        <tr>
                                                            <th className="hidden">Sch Id</th>
                                                            <th></th>
                                                            <th></th>
                                                            <th>Job Id</th>
                                                            <th>Site Address</th>
                                                            <th>Customer</th>
                                                            <th>Item</th>
                                                            <th>Quantity</th>
                                                            <th>Hire Period</th>
                                                            <th>On/Off Hire</th>
                                                            <th>End Hire</th>
                                                            <th>Pickup</th>
                                                            <th>Bring Forward</th>
                                                            <th>Notes</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {this.state.results.map((result, index) =>
                                                            <tr key={index} className={result.type === "Install" ? "tableColours--install" : "tableColours--pickup"}>
                                                                <td className="hidden">{result.scheduleId}</td>
                                                                <td>{result.itemIndex + 1}</td>
                                                                <td><input type='checkbox' className="input" checked={result.selected} onChange={(e) => this.selectItem(e, index)} /></td>
                                                                <td>
                                                                    <Link to={'/job/' + result.jobId} target="_blank" >
                                                                        {result.jobId}
                                                                    </Link>
                                                                </td>
                                                                <td>
                                                                    <div className="tooltipitem  tooltipitem__right">{result.siteAddress ? result.siteAddress.substring(0, 30) + "... " : ""}
                                                                        <span className="tooltipitemtext  tooltipitemtext__right">{result.siteAddress}</span>
                                                                    </div>
                                                                </td>
                                                                <td>{result.customer}</td>
                                                                <td>
                                                                    <div className="tooltipitem tooltipitem__right">{result.itemShortcut}
                                                                        <span className="tooltipitemtext tooltipitemtext__right">{result.item}</span>
                                                                    </div>
                                                                </td>
                                                                <td>{result.quantityType}</td>
                                                                <td>{result.hirePeriod}</td>
                                                                <td>
                                                                    <input type="date" min={minDate} className="form-control" value={result.onoffhireDate} onChange={(e) => this.onCellChange(index, "onoffhireDate", e.target.value)} />
                                                                </td>
                                                                <td>
                                                                    <input type="date" min={minDate} className={result.pickupScheduled || result.callout || result.allowOnGoingHire || result.type === "Pick Up" ? "hidden" : "form-control"} value={result.endhireDate} onChange={(e) => this.onCellChange(index, "endhireDate", e.target.value)} />
                                                                </td>
                                                                <td>
                                                                    <input type="date" min={minDate} className={result.pickupScheduled || result.callout || result.allowOnGoingHire || result.type === "Pick Up" ? "hidden" : "form-control"} value={result.pickupDate} onChange={(e) => this.onCellChange(index, "pickupDate", e.target.value)} />
                                                                </td>
                                                                <td>
                                                                    <input type='date' min={minDate} className="form-control" value={result.bringForwardDate} onChange={(e) => this.onCellChange(index, "bringForwardDate", e.target.value)} />
                                                                </td>
                                                                <td>
                                                                    <div className="tooltipitem  tooltipitem__left">{result.notes ? result.notes.substring(0, 20) + "... " : ""}
                                                                        <span className="tooltipitemtext  tooltipitemtext__left">{result.notes}</span>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {this.state.errors["results"] ?
                                                (this.state.errors["results"]).split("\n").map((item: any, key: any) => {
                                                    return <span className="label errors" key={key}>{item}<br /></span>
                                                })
                                                : ""}
                                            <span className={this.state.results.length == 0 ? "" : "hidden"}>There were no matching jobs found. Try modifying your search criteria.</span>
                                        </div>
                                    </div>
                                    <div className={this.state.itemType === "manual" ? "" : "hidden"}>
                                        {manualJob}
                                    </div>
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <button className={this.state.showConfirmDetails ? "hidden" : "popup-button--yes"} onClick={this.schedule}>Schedule</button>
                        </Modal.Footer>
                    </Modal>
                </div>
            </div>
        );
    }
}